/**
 * JobsScanStatus - Pause/Resume scan control component
 *
 * Manages scan freeze/unfreeze state with confirmation modal.
 * Renders as a rounded button to match other action buttons in the Jobs table.
 */

import { PauseCircleIcon, PlayCircleIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { Tooltip } from '@/components/Tooltip';
import { useScanLevel } from '@/hooks/useScanLevel';
import { cn } from '@/utils/classname';

export interface JobsScanStatusProps {
  /** Next run text for tooltip display */
  nextRunText: string;
  /** Callback to refetch jobs after status change */
  refetch: () => void;
  /** Optional email for checking frozen status of specific customer (for impersonation) */
  email?: string;
}

/**
 * JobsScanStatus component
 *
 * Provides a rounded button to pause/resume automated scans with a confirmation modal.
 * The button shows contextual styling:
 * - "Pause Scans" - Red/danger styling (destructive action)
 * - "Resume Scans" - Neutral styling (safe action)
 */
export const JobsScanStatus = ({
  nextRunText,
  refetch,
  email,
}: JobsScanStatusProps) => {
  const [showUpdateJobStatus, setShowUpdateJobStatus] = useState(false);
  const {
    isFrozen,
    freeze,
    unfreeze,
    isUpdating: isJobStatusLoading,
  } = useScanLevel({ email });
  const JobIcon = isFrozen ? PlayCircleIcon : PauseCircleIcon;

  return (
    <div data-testid="jobs-scan-status">
      <Loader
        isLoading={isJobStatusLoading}
        className={cn('h-[32px]', isFrozen ? 'w-[144px]' : 'w-[130px]')}
      >
        <Tooltip
          placement="top"
          title={
            isFrozen
              ? `Resume the creation of new automatic scans. ${nextRunText}`
              : 'Pause the creation of new automatic scans. Existing scans will run to completion. Manually initiated scans will still be available.'
          }
        >
          <div>
            <Button
              data-testid="jobs-scan-status-button"
              size="sm"
              onClick={() => setShowUpdateJobStatus(true)}
              variant="outline"
              className={cn(
                'rounded-full',
                // Danger styling for Pause state (destructive action)
                // Uses Chariot semantic tokens for theme-aware solid red with white text
                // border-transparent overrides the purple border from rounded styleType
                !isFrozen &&
                  'bg-chariot-status-critical hover:bg-chariot-status-critical/80 border-transparent text-white'
              )}
              aria-label={
                isFrozen ? 'Resume automated scans' : 'Pause automated scans'
              }
            >
              <JobIcon className="size-4" />
              {isFrozen ? 'Resume Scans' : 'Pause Scans'}
            </Button>
          </div>
        </Tooltip>
      </Loader>
      <Modal
        title=""
        hideClose
        hideFooter
        open={showUpdateJobStatus}
        onClose={() => setShowUpdateJobStatus(false)}
        style="dialog"
        size="xl"
      >
        <div
          className="flex flex-col items-center gap-4 px-16 py-10"
          data-testid="jobs-modal"
        >
          <p className="text-lg font-bold">
            {`Are you sure you want to ${isFrozen ? 'resume' : 'pause'} the automated jobs?`}
          </p>
          <p className="text-xs">
            {isFrozen
              ? "You can pause anytime you'd like"
              : "You can resume anytime you'd like"}
          </p>
          <div className="flex gap-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setShowUpdateJobStatus(false);
              }}
              data-testid="modal-cancel-button"
              aria-label="Cancel and close dialog"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="px-5"
              onClick={async () => {
                setShowUpdateJobStatus(false);
                if (isFrozen) {
                  await unfreeze();
                } else {
                  await freeze();
                }
                refetch();
              }}
              data-testid="modal-primary-button"
            >
              <JobIcon className="size-5" />
              {isFrozen ? 'Resume Jobs' : 'Pause Jobs'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
